import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/Products.css';

const features = [
  {
    title: 'Create multi-card widgets',
    description: 'Build a configurable widget containing multiple cards for presenting related products or product choices.',
  },
  {
    title: 'Map cards to products',
    description: 'Connect an individual card to a Shopify product as its destination and presentation target.',
  },
  {
    title: 'Map cards to variants',
    description: 'Choose a specific product variant when a card needs to represent a more precise product option.',
  },
  {
    title: 'Manage card order',
    description: 'Reorder and manage cards within a widget so merchants can control how the configured options are presented.',
  },
  {
    title: 'Choose the mapping target',
    description: 'Select whether each card should map to a complete product or to one of its specific variants.',
  },
  {
    title: 'Targeted widget presentation',
    description: 'Configure product- or variant-specific widget presentation based on the selected mapping approach.',
  },
];

const useCases = [
  'Presenting a curated group of related Shopify products in one widget',
  'Giving customers direct access to specific variants through individual cards',
  'Organizing product choices into a deliberate, merchant-controlled sequence',
  'Creating card-based product discovery experiences for selected products or variants',
];

const mappingOptions = [
  {
    title: 'Product mapping',
    description: 'A card points to a Shopify product when the full product is the intended target.',
  },
  {
    title: 'Variant mapping',
    description: 'A card points to a specific variant when the intended target is a particular product option.',
  },
];

const ProductWidget = () => {
  return (
    <main className="product-detail">
      <SEO
        title="Shopify Product Widget & Variant Mapping | Bularis Tech"
        description="Create Shopify product card widgets, manage card order, and map cards to products or specific product variants."
        path="/products/product-widget"
      />
      <header className="product-hero">
        <span className="products-eyebrow">Shopify app by Bularis Tech</span>
        <h1>Product Widget</h1>
        <p>
          Create configurable Shopify product card widgets and map each card to a product or a
          specific product variant.
        </p>
      </header>

      <div className="product-content">
        <section className="product-section product-introduction">
          <div>
            <h2>Build more intentional product presentations</h2>
            <p>
              Standard product navigation does not always provide the focused presentation a
              merchant needs. Product Widget brings multiple cards into a configurable widget,
              giving merchants control over card order and the Shopify products or variants each
              card represents.
            </p>
          </div>
          <aside className="product-summary" aria-label="App summary">
            <span>Product type</span>
            <strong>Shopify app</strong>
            <span>Core purpose</span>
            <strong>Product and variant widget mapping</strong>
          </aside>
        </section>

        <section className="product-section">
          <div className="product-section-heading">
            <span className="products-eyebrow">Core capabilities</span>
            <h2>Configure cards around your Shopify catalog</h2>
          </div>
          <div className="product-features">
            {features.map((feature) => (
              <article className="product-feature" key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="product-section">
          <div className="product-section-heading">
            <span className="products-eyebrow">Mapping approach</span>
            <h2>Choose the right target for each card</h2>
            <p>
              While configuring a card, the merchant chooses whether its mapping target is a
              product or a specific variant. That selection connects the card to the intended
              catalog item and supports the corresponding widget presentation.
            </p>
          </div>
          <div className="product-features">
            {mappingOptions.map((option) => (
              <article className="product-feature" key={option.title}>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="product-section">
          <h2>Shopify use cases</h2>
          <ul className="product-use-cases">
            {useCases.map((useCase) => <li key={useCase}>{useCase}</li>)}
          </ul>
        </section>

        <section className="product-cta">
          <div>
            <h2>Discuss Product Widget for your store</h2>
            <p>Tell us how you want to organize and map product cards in your Shopify experience.</p>
          </div>
          <Link to="/contact">Contact Bularis Tech</Link>
        </section>
      </div>
    </main>
  );
};

export default ProductWidget;
